import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/** The longest a colour change waits for its new images before swapping anyway, in ms. */
const DECODE_TIMEOUT = 3000;

/**
 * The gallery's media, in order. Two galleries with the same key show the same images.
 * @param {Element} gallery
 */
function mediaKey(gallery) {
  return Array.from(gallery.querySelectorAll('.product-media[data-media-id]'), (media) =>
    media.getAttribute('data-media-id')
  ).join(',');
}

/**
 * Resolves once every image has decoded (or failed to), or after `timeout` ms.
 * @param {HTMLImageElement[]} images
 * @param {number} timeout
 */
function imagesDecoded(images, timeout) {
  const decoded = images.map((image) => {
    // These are on screen right after the swap, so they mustn't wait for lazy loading.
    if (image.loading === 'lazy') image.loading = 'eager';
    return image.decode().catch(() => {});
  });

  return Promise.race([Promise.all(decoded), new Promise((resolve) => setTimeout(resolve, timeout))]);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /** Bumped on every variant update, so a swap still waiting on its images can tell it's stale. */
  #swapId = 0;

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * A size change within the same colour shows the same media, and replacing the gallery then
   * re-created every image, which flashed white while they decoded. So the gallery is only
   * replaced when the media actually change (a colour change), and only once the new images that
   * will be on screen have decoded: until then the old ones stay up.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Before the comparison, so a newer update showing the current media cancels a pending swap.
    const swapId = ++this.#swapId;

    if (mediaKey(newMediaGallery) === mediaKey(this)) return;

    // Adopted, its images start loading off-page; custom elements still upgrade on insertion.
    const incoming = /** @type {HTMLElement} */ (document.adoptNode(newMediaGallery));
    await imagesDecoded(this.#imagesShownAfterSwap(incoming), DECODE_TIMEOUT);

    if (swapId !== this.#swapId || !this.isConnected) return;

    this.replaceWith(incoming);

    // Safety net: after the new gallery is in the DOM, force its slideshow to land
    // on the selected variant's featured media. The server-side filter already puts
    // that media at slide 0, but this guards against future re-renders or races
    // where the fresh slideshow might initialize to a different slide.
    const variantMediaId = incoming.dataset.variantMediaId;
    if (!variantMediaId) return;

    requestAnimationFrame(() => {
      const slideshow = incoming.querySelector('slideshow-component');
      if (slideshow && typeof slideshow.select === 'function') {
        try {
          slideshow.select({ id: variantMediaId }, undefined, { animate: false });
        } catch (_) {
          /* slideshow may not be fully upgraded yet; safe to ignore */
        }
      }
    });
  };

  /**
   * The images in an incoming gallery that will be on screen as soon as it replaces this one.
   * @param {HTMLElement} incoming
   * @returns {HTMLImageElement[]}
   */
  #imagesShownAfterSwap(incoming) {
    /** @type {HTMLImageElement[]} */
    const images = [];

    // Carousel (phones, and the carousel layout): the new one opens on its first slide.
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow?.getClientRects().length) {
      const image = incoming.querySelector('slideshow-slide img.product-media__image');
      if (image instanceof HTMLImageElement) images.push(image);
    }

    // Grid (desktop): the new images that will sit where the ones on screen are now.
    const newItems = incoming.querySelectorAll('.media-gallery__grid > li');
    this.querySelectorAll('.media-gallery__grid > li').forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      if (!rect.height || rect.bottom <= 0 || rect.top >= window.innerHeight) return;

      const image = newItems[index]?.querySelector('img.product-media__image');
      if (image instanceof HTMLImageElement) images.push(image);
    });

    return images;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
